import { Row, Col, Typography, Table, Button, message } from "antd";
import { routes } from "common/routes";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  cancelProcessingPO,
  removeProcessingPO,
  saveProcessingPO,
} from "store/po/poSlice";

const MESSAGE_KEY = "SAVE_PO";

const POSavePage = () => {
  useDocumentTitle("Save Purchase Order");
  const { processingPO, status, error } = useAppSelector((state) => state.po);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingPO;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingPO());
      }
    }

    if (!Object.keys(processingPO).length) {
      navigate(`/${routes.PO.UPLOAD}`);
    }
  }, [dispatch, processingPO, navigate]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "saving purchase order", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Purchase Order saved!",
        duration: 5,
        key: MESSAGE_KEY,
      });
    } else if (status === "cancelling") {
      message.loading({
        content: "cancelling purchase order",
        key: MESSAGE_KEY,
      });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  let content;
  const { headerMapping } = processingPO;
  const { Text } = Typography;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMapping),
    [headerMapping]
  );

  const onSavePO = () => {
    dispatch(saveProcessingPO());
  };

  const onCancelSave = () => {
    dispatch(cancelProcessingPO());
  };

  if (!Object.keys(processingPO).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const successRows = processingPO.successRows;
    const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = [
      ...Object.keys(headers).map(
        (key) =>
          ({
            key,
            title: headers[key],
            dataIndex: key,
          } as any)
      ),
    ];
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        scroll={{ y: 500, x: "100vh" }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={24}>
                <Row>
                  <Col>
                    <Button
                      type="primary"
                      onClick={onSavePO}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                    >
                      Save PO
                    </Button>
                  </Col>
                  <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                    <Button
                      onClick={onCancelSave}
                      disabled={status === "loading"}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      ></Table>
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Purchase Order" />
        </Col>
      </Row>
      <Row>
        <Col xs={24}>{content}</Col>
      </Row>
    </>
  );
};

export default POSavePage;
